import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  BatchCostReportRecord,
  CostActivity,
  CostEntryRecord,
  CostType,
  IsoDate,
  NumericString,
} from '@henlytics/types'
import { api } from '../lib/api-client'
import { queryKeys } from '../lib/query-keys'
import { clientUuid } from '../lib/uuid'

/**
 * The batch's full cost picture, computed by `packages/costing` on the server.
 * Nothing in the frontend recalculates any of it (§9 rule 6).
 */
export function useBatchCost(batchId: string | undefined, asOf: IsoDate) {
  return useQuery({
    queryKey: queryKeys.batchCost(batchId ?? '', asOf),
    queryFn: ({ signal }) =>
      api.get<BatchCostReportRecord>(`/reports/batches/${batchId}/cost?asOf=${asOf}`, signal),
    enabled: Boolean(batchId),
  })
}

export function useCostEntries(batchId?: string) {
  return useQuery({
    queryKey: queryKeys.costEntries(batchId),
    queryFn: ({ signal }) =>
      api.get<CostEntryRecord[]>(batchId ? `/cost-entries?batchId=${batchId}` : '/cost-entries', signal),
  })
}

export interface NewCostEntry {
  /** Null for a shared cost — the API allocates it by bird-days (§3.7). */
  batchId?: string | null
  costDate: IsoDate
  costType: CostType
  activity: CostActivity
  amount: NumericString
  hours?: NumericString | null
  periodStart?: IsoDate | null
  periodEnd?: IsoDate | null
  notes?: string | null
  clientUuid?: string
}

export function useCreateCostEntry(batchId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NewCostEntry) =>
      api.post<CostEntryRecord>('/cost-entries', {
        ...input,
        clientUuid: input.clientUuid ?? clientUuid(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.costEntries(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.costEntries() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}

export function useVoidCostEntry(batchId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entryId: string) => api.post<CostEntryRecord>(`/cost-entries/${entryId}/void`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.costEntries(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.costEntries() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}
